import { useEffect } from 'react';
import {
  buildFullTitle,
  resolveOgImageUrl,
  type PageMeta,
} from '@visualry/shared';

type SeoHeadProps = Partial<Omit<PageMeta, 'path' | 'jsonLd'>> & {
  /** Public app URL used to build absolute canonical/OG URLs. */
  publicAppUrl: string;
  /** Page path used to build the canonical URL (defaults to the page meta path). */
  path?: string;
  /**
   * Additional JSON-LD blocks to embed. Merged with any blocks returned
   * by the matching PageMeta.jsonLd factory. Each entry is a schema.org
   * object and is emitted as its own <script type="application/ld+json">.
   */
  extraJsonLd?: unknown[];
};

type MetaTag = { attr: 'name' | 'property' | 'http-equiv'; key: string; value: string };

const upsertMeta = (head: HTMLHeadElement, tag: MetaTag) => {
  const selector = `meta[${tag.attr}="${tag.key}"]`;
  let el = head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(tag.attr, tag.key);
    head.appendChild(el);
  }
  el.setAttribute('content', tag.value);
};

const upsertLink = (head: HTMLHeadElement, rel: string, href: string) => {
  const selector = `link[rel="${rel}"]`;
  let el = head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const upsertJsonLd = (head: HTMLHeadElement, id: string, data: unknown) => {
  const selector = `script[data-seo-json-ld="${id}"]`;
  let el = head.querySelector<HTMLScriptElement>(selector);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo-json-ld', id);
    head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeJsonLd = (head: HTMLHeadElement) => {
  head.querySelectorAll('script[data-seo-json-ld]').forEach((el) => el.remove());
};

const applyMeta = (input: {
  title: string;
  description: string;
  canonicalUrl: string;
  ogImageUrl: string;
  noindex?: boolean;
  jsonLd: unknown[];
}) => {
  const head = document.head;
  document.title = input.title;

  upsertMeta(head, { attr: 'name', key: 'description', value: input.description });
  upsertMeta(head, { attr: 'property', key: 'og:title', value: input.title });
  upsertMeta(head, { attr: 'property', key: 'og:description', value: input.description });
  upsertMeta(head, { attr: 'property', key: 'og:image', value: input.ogImageUrl });
  upsertMeta(head, { attr: 'property', key: 'og:url', value: input.canonicalUrl });
  upsertMeta(head, { attr: 'property', key: 'og:type', value: 'website' });
  upsertMeta(head, { attr: 'property', key: 'og:site_name', value: 'Visualry' });
  upsertMeta(head, { attr: 'name', key: 'twitter:card', value: 'summary_large_image' });
  upsertMeta(head, { attr: 'name', key: 'twitter:title', value: input.title });
  upsertMeta(head, { attr: 'name', key: 'twitter:description', value: input.description });
  upsertMeta(head, { attr: 'name', key: 'twitter:image', value: input.ogImageUrl });
  upsertMeta(head, {
    attr: 'name',
    key: 'robots',
    value: input.noindex ? 'noindex, follow' : 'index, follow',
  });
  upsertLink(head, 'canonical', input.canonicalUrl);

  removeJsonLd(head);
  input.jsonLd.forEach((block, index) => {
    upsertJsonLd(head, `${index}`, block);
  });
};

// App-wide default meta. Mirrors the tags declared in apps/web/index.html
// so a page that mounts <SeoHead title="" description="">, or that
// navigates away from a page with custom meta, falls back to the same
// state the static SPA shell would have served.
const APP_DEFAULTS = {
  title: 'Visualry - Discover prompts behind stunning AI images',
  description:
    'Visualry - discover prompts behind stunning AI images. Browse curated AI-generated images, reveal the prompts for free, and generate similar visuals.',
  canonicalPath: '/',
  ogTitle: 'Visualry - Discover prompts behind stunning AI images',
  ogDescription:
    'Browse curated AI-generated images, reveal the prompts for free, and generate similar visuals when you need them.',
  ogImagePath: '/og-default.png',
  ogLocale: 'en_US',
  robots: 'index, follow',
} as const;

const restoreDefaults = (publicAppUrl: string) => {
  const head = document.head;
  const canonicalUrl = new URL(APP_DEFAULTS.canonicalPath, publicAppUrl).toString();
  const ogImageUrl = new URL(APP_DEFAULTS.ogImagePath, publicAppUrl).toString();

  document.title = APP_DEFAULTS.title;
  upsertMeta(head, { attr: 'name', key: 'description', value: APP_DEFAULTS.description });
  upsertMeta(head, { attr: 'property', key: 'og:title', value: APP_DEFAULTS.ogTitle });
  upsertMeta(head, { attr: 'property', key: 'og:description', value: APP_DEFAULTS.ogDescription });
  upsertMeta(head, { attr: 'property', key: 'og:image', value: ogImageUrl });
  upsertMeta(head, { attr: 'property', key: 'og:url', value: canonicalUrl });
  upsertMeta(head, { attr: 'property', key: 'og:type', value: 'website' });
  upsertMeta(head, { attr: 'property', key: 'og:site_name', value: 'Visualry' });
  upsertMeta(head, { attr: 'property', key: 'og:locale', value: APP_DEFAULTS.ogLocale });
  upsertMeta(head, { attr: 'name', key: 'twitter:card', value: 'summary_large_image' });
  upsertMeta(head, { attr: 'name', key: 'twitter:title', value: APP_DEFAULTS.ogTitle });
  upsertMeta(head, { attr: 'name', key: 'twitter:description', value: APP_DEFAULTS.ogDescription });
  upsertMeta(head, { attr: 'name', key: 'twitter:image', value: ogImageUrl });
  upsertMeta(head, { attr: 'name', key: 'robots', value: APP_DEFAULTS.robots });
  upsertLink(head, 'canonical', canonicalUrl);
  removeJsonLd(head);
};

/**
 * <SeoHead> updates the document head with the page's title, meta
 * description, Open Graph, Twitter Card, canonical link, and JSON-LD.
 *
 * It is the runtime companion to the Worker dispatcher's bot HTML — the
 * two share the same source of truth (PageMeta from @visualry/shared)
 * so a page's title and description match whether the visitor is Googlebot
 * or a real user.
 *
 * Mount it once at the top of each page component:
 *
 *   <SeoHead publicAppUrl={...} title="..." description="..." />
 *
 * The component is a no-render side-effect, so it returns null and never
 * affects layout.
 */
export const SeoHead = ({
  publicAppUrl,
  path,
  title,
  description,
  ogImage,
  noindex,
  extraJsonLd,
}: SeoHeadProps) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!title || !description) {
      restoreDefaults(publicAppUrl);
      return;
    }
    const canonicalPath = path ?? '/';
    const canonicalUrl = new URL(canonicalPath, publicAppUrl).toString();
    const ogImageUrl = resolveOgImageUrl(ogImage, publicAppUrl);
    const fullTitle = buildFullTitle(title);
    const jsonLd = extraJsonLd ?? [];
    applyMeta({
      title: fullTitle,
      description,
      canonicalUrl,
      ogImageUrl,
      noindex,
      jsonLd,
    });
    // Reset to app defaults on unmount so navigating from a page with
    // custom meta (e.g. /pricing) to a page that doesn't mount its own
    // <SeoHead> (e.g. /signin) doesn't leave stale og:* / twitter:* /
    // canonical / description tags behind. If the next page mounts its
    // own <SeoHead>, that effect runs and overwrites these defaults.
    return () => restoreDefaults(publicAppUrl);
  }, [publicAppUrl, path, title, description, ogImage, noindex, extraJsonLd]);

  return null;
};
